/**
 * Cat din videoclip a fost VAZUT efectiv — nu unde a ajuns cursorul.
 *
 * De ce exista modulul asta, si de ce e pur:
 * evenimentul `ENDED` al playerului se poate obtine tragand cursorul la final in
 * doua secunde. Cerinta e „cap-coada", deci se aduna SEGMENTELE parcurse: se
 * esantioneaza pozitia, iar doua esantioane consecutive apropiate inseamna
 * vizionare (se adauga intervalul dintre ele); un salt inseamna derulare, si nu
 * se adauga nimic. Derularea ramane permisa — doar ca nu se numara.
 *
 * Fiind pur, se poate testa fara sa montam nimic: `vitest.config.ts` ruleaza pe
 * `environment: 'node'`, deci un test de componenta n-ar avea `document`.
 */

/** Interval [start, sfarsit], in secunde, ambele capete inclusive. */
export interface Segment {
    start: number;
    sfarsit: number;
}

/**
 * Cat de departe pot fi doua esantioane consecutive ca sa fie considerate
 * vizionare continua. Sondajul merge la ~250 ms; pragul e deliberat mai mare
 * decat pasul, ca o intarziere de fir de executie sa nu rupa un segment real,
 * dar mult sub un salt de derulare, care muta cursorul cu secunde.
 */
export const PRAG_CONTINUITATE = 1.5;

/** Acoperirea de la care se deschide poarta. Cerinta de produs: „cap-coada". */
export const ACOPERIRE_NECESARA = 0.95;

/**
 * Adauga un esantion la lista de segmente vazute.
 *
 * `pozitieAnterioara` e ultima pozitie citita; `pozitie` e cea curenta. Daca
 * distanta dintre ele depaseste `PRAG_CONTINUITATE`, intervalul NU se adauga:
 * a fost un salt, nu vizionare.
 */
export function adaugaEsantion(
    segmente: Segment[],
    pozitieAnterioara: number | null,
    pozitie: number
): Segment[] {
    if (pozitieAnterioara === null) return segmente;
    if (!Number.isFinite(pozitieAnterioara) || !Number.isFinite(pozitie)) return segmente;

    const start = Math.min(pozitieAnterioara, pozitie);
    const sfarsit = Math.max(pozitieAnterioara, pozitie);

    // Salt (inainte sau inapoi): derularea e permisa, dar nu se numara ca vizionare.
    if (sfarsit - start > PRAG_CONTINUITATE) return segmente;
    // Playerul pe pauza da acelasi timp de doua ori: nimic de adaugat.
    if (sfarsit === start) return segmente;

    return uneste([...segmente, { start, sfarsit }]);
}

/**
 * Unifica intervalele care se suprapun sau se ating, ca sa nu numaram de doua ori.
 *
 * ⚠️ PROPRIETATE CUNOSCUTA, asumata: doua segmente despartite de un gol de cel
 * mult `PRAG_CONTINUITATE` (1,5 s) se lipesc, deci golul se numara ca vazut.
 * Acoperirea poate astfel supraevalua cu pana la 1,5 s pentru fiecare gol de
 * felul asta. E acelasi prag ca la `adaugaEsantion`, si din acelasi motiv: un
 * esantion pierdut (fila in fundal, un cadru sarit, o incetinire) n-are voie sa
 * taie vizionarea in bucati care nu se mai inchid niciodata. Alternativa —
 * lipirea doar la contact exact — ar fi facut poarta imposibil de trecut pe un
 * telefon lent, ceea ce e o eroare mai scumpa decat 1,5 s de indulgenta.
 */
export function uneste(segmente: Segment[]): Segment[] {
    if (segmente.length <= 1) return [...segmente];
    const sortate = [...segmente].sort((a, b) => a.start - b.start);
    const out: Segment[] = [sortate[0]];
    for (const s of sortate.slice(1)) {
        const ultim = out[out.length - 1];
        if (s.start <= ultim.sfarsit + PRAG_CONTINUITATE) {
            ultim.sfarsit = Math.max(ultim.sfarsit, s.sfarsit);
        } else {
            out.push({ ...s });
        }
    }
    return out;
}

/** Secundele vazute, fara dubluri. */
export function secundeVazute(segmente: Segment[]): number {
    return uneste(segmente).reduce((n, s) => n + (s.sfarsit - s.start), 0);
}

/**
 * Acoperirea, intre 0 si 1. Cu durata necunoscuta (playerul inca n-a raportat-o)
 * intoarce 0 — niciodata 1: o poarta care se deschide cand nu poate masura e mai
 * rea decat una care lipseste.
 */
export function acoperire(segmente: Segment[], durata: number): number {
    if (!Number.isFinite(durata) || durata <= 0) return 0;
    return Math.min(1, secundeVazute(segmente) / durata);
}

/** Poarta: s-a vazut destul cat sa se poata continua? */
export function poateContinua(segmente: Segment[], durata: number): boolean {
    return acoperire(segmente, durata) >= ACOPERIRE_NECESARA;
}

/** Secundele ramase de vazut pana la prag, rotunjite in sus. */
export function secundeRamase(segmente: Segment[], durata: number): number {
    if (!Number.isFinite(durata) || durata <= 0) return 0;
    const necesar = durata * ACOPERIRE_NECESARA;
    return Math.max(0, Math.ceil(necesar - secundeVazute(segmente)));
}

/** `m:ss`, pentru eticheta butonului si pentru cipul cu durata. */
export function formateazaDurata(secunde: number): string {
    if (!Number.isFinite(secunde) || secunde < 0) return '0:00';
    const total = Math.round(secunde);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m + ':' + String(s).padStart(2, '0');
}

/**
 * Bara de sub player, ca in macheta: `nrCasute` casute egale, fiecare aprinsa
 * daca portiunea ei a fost vazuta. NU e o bara de pozitie — golul din mijloc e
 * exact mesajul: „aici ai sarit, poti relua de unde e goala bara".
 *
 * O casuta se considera vazuta la >= jumatate acoperita: cu pragul la 100% o
 * singura secunda lipsa ar stinge casuta si bara ar parea mai goala decat e;
 * cu pragul la orice atingere, un salt care traverseaza casuta ar aprinde-o
 * degeaba. Jumatatea e alegerea care nu minte in niciuna din directii.
 */
export function casuteVizionate(segmente: Segment[], durata: number, nrCasute = 12): boolean[] {
    const goale = Array.from({ length: nrCasute }, () => false);
    if (!Number.isFinite(durata) || durata <= 0) return goale;
    const pas = durata / nrCasute;
    const unite = uneste(segmente);
    return goale.map((_, i) => {
        const de = i * pas;
        const pana = (i + 1) * pas;
        const acoperit = unite.reduce((n, s) => {
            const start = Math.max(s.start, de);
            const sfarsit = Math.min(s.sfarsit, pana);
            return n + Math.max(0, sfarsit - start);
        }, 0);
        return acoperit >= pas / 2;
    });
}

/**
 * Cat poate sari cursorul inainte de punctul cel mai departat vazut, fara sa fie refuzat.
 * Mai mare decat pasul sondajului (250 ms) si decat `PRAG_CONTINUITATE`, ca o intarziere de fir
 * sau un esantion pierdut sa nu declanseze o intoarcere pe o redare normala.
 */
export const TOLERANTA_SALT = 2;

/** Ce spune garda dupa un esantion. */
export interface VerdictSalt {
    /** Cel mai departe punct atins legitim, dupa esantionul asta. */
    maxVazut: number;
    /** Unde trebuie intors playerul; `null` = nimic de facut. */
    revinoLa: number | null;
}

/**
 * Garda „înapoi DA, înainte NU" (decizia owner-ului, cererile ECOTIC din 21.09, item 2).
 *
 * Pana acum derularea era permisa si doar nu se numara (vezi capul fisierului). De acum, un salt
 * dincolo de `maxVazut + TOLERANTA_SALT` se refuza: apelantul intoarce playerul la `maxVazut`.
 * Inapoi e liber, si la fel orice salt INAINTE in interiorul portiunii deja vazute.
 *
 * `secundeRedare` = cat timp REAL a rulat playerul intre esantionul anterior si acesta (0 daca era
 * pe pauza). Fara el, o fila tinuta in fundal ar fi refuzata pe nedrept: browserul rareste
 * `setInterval` in fundal (o data pe secunda, si mai rar dupa cateva minute in Chrome), dar filmul
 * merge mai departe, deci doua esantioane consecutive pot fi la zeci de secunde distanta dupa o
 * vizionare perfect cinstita. Avansul permis creste cu exact cat a avut timp sa ruleze — nu mai
 * mult. Apelantul inmulteste deja cu viteza de redare, daca o stie.
 *
 * `durata` (optionala) plafoneaza `maxVazut`: un esantion citit dupa sfarsit nu-l duce peste film.
 *
 * Pura, fara ceas propriu: timpul vine ca parametru, ca in `stareRetrimitere` (`otp-cod.ts`).
 */
export function gardaSaltInainte(
    maxVazut: number,
    pozitie: number,
    secundeRedare = 0,
    durata = 0
): VerdictSalt {
    const max = Number.isFinite(maxVazut) && maxVazut > 0 ? maxVazut : 0;
    // Un esantion necitibil nu dovedeste nimic, in niciun sens.
    if (!Number.isFinite(pozitie)) return { maxVazut: max, revinoLa: null };

    const avans = Number.isFinite(secundeRedare) && secundeRedare > 0 ? secundeRedare : 0;
    if (pozitie > max + TOLERANTA_SALT + avans) {
        return { maxVazut: max, revinoLa: max };
    }

    const nou = Math.max(max, pozitie);
    return {
        maxVazut: Number.isFinite(durata) && durata > 0 ? Math.min(nou, durata) : nou,
        revinoLa: null
    };
}

/** „2 min 40 s" / „40 s" — forma din macheta, pentru cipul cu durata declarata. */
export function formateazaDurataLunga(secunde: number): string {
    if (!Number.isFinite(secunde) || secunde <= 0) return '0 s';
    const total = Math.round(secunde);
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return s + ' s';
    return s === 0 ? m + ' min' : m + ' min ' + s + ' s';
}
